import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  NewspaperIcon,
  MapPinIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import CountUp from 'react-countup';
import { supabase } from '../lib/supabase';
import { getPlayersByTournament, getPartnersByTournament, getTournamentEvents } from '../lib/supabase';

interface EditorDashboardHomeProps {
  tournamentId: string;
}

interface EditorStats {
  players: number;
  partners: number;
  events: { total: number; upcoming: number };
  contentSections: number;
  pressRegistrations: number;
  courses: number;
}

function EditorDashboardHome({ tournamentId }: EditorDashboardHomeProps) {
  const [stats, setStats] = useState<EditorStats>({
    players: 0,
    partners: 0,
    events: { total: 0, upcoming: 0 },
    contentSections: 0,
    pressRegistrations: 0,
    courses: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tournamentId) fetchStats();
  }, [tournamentId]);

  const fetchStats = async () => {
    try {
      setLoading(true);

      const [playersRes, partnersRes, eventsRes, contentRes, pressRes, coursesRes] = await Promise.all([
        getPlayersByTournament(tournamentId),
        getPartnersByTournament(tournamentId),
        getTournamentEvents(tournamentId),
        supabase.from('content_sections').select('id', { count: 'exact', head: true }),
        supabase.from('press_registrations').select('id', { count: 'exact', head: true }).eq('tournament_id', tournamentId),
        supabase.from('courses').select('id', { count: 'exact', head: true }),
      ]);

      const now = new Date();
      const upcoming = eventsRes.data?.filter(e => new Date(e.date) > now).length || 0;

      setStats({
        players: playersRes.data?.length || 0,
        partners: partnersRes.data?.length || 0,
        events: { total: eventsRes.data?.length || 0, upcoming },
        contentSections: contentRes.count || 0,
        pressRegistrations: pressRes.count || 0,
        courses: coursesRes.count || 0,
      });
    } catch (err) {
      console.error('Error fetching editor stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
      </div>
    );
  }

  const cards = [
    { label: 'Content Sections', value: stats.contentSections, icon: DocumentTextIcon, color: 'text-blue-600', bg: 'bg-blue-500/10' },
    { label: 'Players', value: stats.players, icon: UserGroupIcon, color: 'text-teal-600', bg: 'bg-teal-500/10' },
    { label: 'Partners', value: stats.partners, icon: BuildingOfficeIcon, color: 'text-orange-600', bg: 'bg-orange-500/10' },
    { label: 'Upcoming Events', value: stats.events.upcoming, icon: CalendarIcon, color: 'text-green-600', bg: 'bg-green-500/10', sub: `${stats.events.total} total` },
    { label: 'Press Registrations', value: stats.pressRegistrations, icon: NewspaperIcon, color: 'text-rose-600', bg: 'bg-rose-500/10' },
    { label: 'Courses', value: stats.courses, icon: MapPinIcon, color: 'text-sky-600', bg: 'bg-sky-500/10' },
  ];

  const quickLinks = [
    { label: 'Manage Content', description: 'Edit pages & sections', tab: 'contents', icon: DocumentTextIcon },
    { label: 'View Players', description: 'Browse player list', tab: 'players', icon: UserGroupIcon },
    { label: 'Manage Partners', description: 'View partnerships', tab: 'partners', icon: BuildingOfficeIcon },
    { label: 'Press Submissions', description: 'Review media registrations', tab: 'press', icon: NewspaperIcon },
    { label: 'Tournaments', description: 'Tournament details', tab: 'tournaments', icon: TrophyIcon },
    { label: 'Courses', description: 'Course information', tab: 'courses', icon: MapPinIcon },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="bg-golf-orange/10 p-2 rounded-lg">
          <DocumentTextIcon className="w-5 h-5 text-golf-orange" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-golf-gray">Editor Dashboard</h2>
          <p className="text-sm text-gray-500">Content and tournament management overview</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: i * 0.07 }}
            className="bg-white rounded-xl shadow-sm p-5 border border-gray-200"
          >
            <div className="flex items-center gap-3">
              <div className={`${card.bg} p-2.5 rounded-xl`}>
                <card.icon className={`w-5 h-5 ${card.color}`} />
              </div>
              <div>
                <p className="text-xs text-gray-500">{card.label}</p>
                <div className="flex items-baseline gap-1.5">
                  <CountUp end={card.value} duration={1.5} className="text-xl font-bold text-golf-gray" />
                  {card.sub && <span className="text-xs text-gray-400">{card.sub}</span>}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-golf-gray">Quick Access</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
          {quickLinks.map(link => (
            <button
              key={link.tab}
              onClick={() => {
                const event = new CustomEvent('navigate-tab', { detail: link.tab });
                window.dispatchEvent(event);
              }}
              className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors text-left"
            >
              <div className="bg-golf-orange/10 p-2 rounded-lg flex-shrink-0">
                <link.icon className="w-4 h-4 text-golf-orange" />
              </div>
              <div>
                <p className="text-sm font-medium text-golf-gray">{link.label}</p>
                <p className="text-xs text-gray-500">{link.description}</p>
              </div>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

export default EditorDashboardHome;
